import moment from 'moment';
import { Popover } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { PLATFORM_THEME_COLORS, Table, Tag, Typography } from '@thinknet-gmbh/platform-common-ui';
import { EmployeeQuestionnaireStatusesEnum } from '@thinknet-gmbh/platform-common';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { EMPLOYEE_PAGES } from 'src/pages/employee/employee.constants';

import { QUESTIONNAIRE_STATUSES_STYLES } from '../../open-questionnaires-list.constants';
import OpenQuestionnairesTableRowPopover from './open-questionnaires-table-row-popover.component';

import { IOpenQuestionnairesTableProps } from './open-questionnaires-list-table.interfaces';
import {
  SupervisorDetailsWrapper,
  PopoverContentWrapper,
  SupervisorRow,
} from '../../open-questionnaires-list.styles';

const OpenQuestionnairesListTable = ({
  dataSource,
  paginationConfig,
  handleModals,
  onTableChange,
  loading,
}: IOpenQuestionnairesTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'employee']);

  return (
    <Table
      loading={loading}
      dataSource={dataSource}
      pagination={paginationConfig}
      rowKey={record => record._id}
      onRow={({ _id }) => ({
        onClick: () =>
          navigate(`${ROUTES.employee}?type=${EMPLOYEE_PAGES.openQuestionnairesDetails}&id=${_id}`),
      })}
      t={t}
    >
      <Table.Column
        title={t('common:labels.fullName')}
        sorter={onTableChange}
        render={({ employeePersonalInfo }) => (
          <Typography>
            {employeePersonalInfo?.firstName} {employeePersonalInfo?.lastName}
          </Typography>
        )}
      />
      <Table.Column
        title={t('employee:labels.company')}
        render={({ company }) => <Typography>{company?.name || '-'}</Typography>}
      />
      <Table.Column
        title={t('common:labels.location')}
        render={({ jobLocation }) => (
          <Typography>{jobLocation?.companyLocation?.name || '-'}</Typography>
        )}
      />
      <Table.Column
        title={t('common:labels.department')}
        render={({ department }) => <Typography>{department?.name || '-'}</Typography>}
      />
      <Table.Column
        title={t('common:labels.jobTitle')}
        render={({ job }) => <Typography>{job?.title || '-'}</Typography>}
      />
      <Table.Column
        title={t('employee:labels.supervisor')}
        render={({ responsiblePersons }) => {
          const displaySupervisor = responsiblePersons
            .map(
              ({
                profile,
              }: {
                profile: {
                  displayName: string;
                };
              }) => {
                return profile?.displayName;
              }
            )
            .join(', ');

          return (
            <SupervisorDetailsWrapper>
              <Popover
                arrowPointAtCenter
                placement="topRight"
                trigger="hover"
                title={''}
                content={
                  <PopoverContentWrapper>
                    <Typography fontColor={PLATFORM_THEME_COLORS.primary.grey}>
                      {displaySupervisor || '-'}
                    </Typography>
                  </PopoverContentWrapper>
                }
              >
                <SupervisorRow>{displaySupervisor || '-'}</SupervisorRow>
              </Popover>
            </SupervisorDetailsWrapper>
          );
        }}
      />
      <Table.Column
        title={t('common:labels.status')}
        render={({ status }) => {
          const { background, fontColor } = QUESTIONNAIRE_STATUSES_STYLES[
            status
              ? (status as EmployeeQuestionnaireStatusesEnum)
              : EmployeeQuestionnaireStatusesEnum.sent
          ];

          return (
            <Tag background={background} fontColor={fontColor}>
              {t(`employee:questionnaire.labels.statuses.${status.toLowerCase()}`)}
            </Tag>
          );
        }}
      />
      <Table.Column
        title={t('common:labels.creationDate')}
        key="createdAt"
        dataIndex="createdAt"
        render={createdAt => <Typography>{moment(createdAt).format('DD.MM.YYYY')}</Typography>}
      />
      <Table.Column
        width={50}
        title={t('common:labels.action')}
        render={record => (
          <OpenQuestionnairesTableRowPopover record={record} handleModals={handleModals} />
        )}
      />
    </Table>
  );
};

export default OpenQuestionnairesListTable;
